import React from "react";
import { Tooltip } from "antd";
import { BUFFER_SPICE, SPICE } from "../../../constants/libraryConstants";
import { ADVANCE, CHECKBOX, INPUT, INPUTANDSELECT, SELECT, SWITCH } from "../../../pages/Andes_v2/AMIModel/SetupComponents/SetupInputGroup";
import { splitValueBlankUnit } from "../../helper/numberHelper";
import { parseSpiceModelSelector } from "../../LibraryHelper/SpiceModelHelper";
import { exsitEnableVoltage } from "../../Rocky";
import { UNIT_OPTIONS } from "../AMIModelHelper";
import { getFileContent, getIbisModelParse, getLibraryFolderChildren } from "../library";
import libraryConstructor from "../library/libraryConstructor";
import { clickBoxPosition } from "./constants";
import { END_TO_END_CHANNEL, PACKAGE } from "../../../constants/treeConstants";
import { getCompSignalName } from "../endToEndChannel/AMIModelHelper";
import { handleComponents } from "./IntegratedHspiceConfig";
import { CONNECTOR, BGA, IC, DIE } from '../../../constants/componentType';

function getNodeSelectOption(modelType, pairs = [], subckt = {}) {
  if (pairs.length === 0 || (subckt.ports || []).length === 0) return [];

  switch (modelType) {
    case 'txModel':
    case 'rxModel':
    case 'rxCpadModel':
    case 'ctleModel':
      const { ports } = subckt;
      const nodeValues = pairs.map(item => item.node)
      return (ports || []).map(item => {
        if (nodeValues.includes(item)) {
          return { name: item, disabled: true }
        }
        return { name: item, disabled: false }
      })
    default:
      return [];
  }
}

// According to the modelName input value find, the modelName containing the lookup field
function getModelNameFilterList(modelNameSelectList, modelSearchValue) {
  if (!modelSearchValue) return modelNameSelectList;
  return modelNameSelectList.filter(item => item.name.includes(modelSearchValue))
}

// selects IBIS file to obtain optional modelSelector, modelName, modelType, fill the model data
async function getIBISModelSelectList({ libraryId: fileId, modelType, modelSelector }, type) {
  if (!fileId) {
    return {
      modelNameSelectList: [],
      modelTypeSelectList: [],
      selectorModels: [],
      modelSearchValue: ''
    }
  }

  const libraryModels = await getIbisModelParse(fileId, type) || {};
  const allSelectorModels = libraryModels.selectorModels || [];
  let models = libraryModels.models || [];
  if (modelSelector) {
    const selectorModel = allSelectorModels.find(item => item.selector === modelSelector) || {};
    models = (selectorModel.models || []).map(item => {
      const modelInfo = models.find(it => it.name === item.name) || {};
      return {
        ...item,
        type: modelInfo.type,
        enable: modelInfo.enable
      }
    })
  }

  if (modelType) {
    models = models.filter(item => item.type === modelType);
  }

  // {name,type,description}
  const modelNameSelectList = models.map(model => {
    return {
      ...model,
      value: model.name,
      title: model.name,
      key: `${model.type} ${model.name}`,
      enableVoltage: exsitEnableVoltage(model.type) ? (model.enable === 'NA' ? '1' : model.enable) : ""
    }
  })

  const modelTypes = (libraryModels.models || []).map(item => item.type);
  return {
    modelNameSelectList,
    modelTypeSelectList: [...new Set(modelTypes)],
    selectorModels: [...allSelectorModels],
    modelSearchValue: ''
  }
}

function getModelSelector(selectorModels, modelName) {
  if (!selectorModels || !selectorModels.length) return "";

  for (const item of selectorModels) {
    const model = item.models.find(item => item.name === modelName);
    if (model) {
      return item.selector;
    }
  }

  return "";
}

async function getSubcktByPairInfo({ libraryId, fileName, subckt }) {
  if (!libraryId || !fileName || !subckt) return {}
  const res = await getFileContent(libraryId, fileName);
  if (!res) return {};
  const subcktList = parseSpiceModelSelector(res);
  const subcktInfo = subcktList.find(item => item.name === subckt) || {};
  return subcktInfo;
}

// To obtain Node data, we need to improve the logic of Net Chapin and then backfill value
function getNodeDataList({
  signalName,
  index,
  hspiceSignalConfig,
  signals = [],
  isShowStimulus,
  channelType,
  controllerChannel,
  deviceChannel,
  contrSignals,
  deviceSignals,
  expandModelConnectWidth,
  expandModelConnectVisible
}) {
  const signalInfo = signals.find(item => item.name === signalName) || {}
  if (!signalInfo || !hspiceSignalConfig || !Object.keys(hspiceSignalConfig).length) {
    return { txNodeDataList: [], txNetDataList: [], z0DataList: [], ctleDataList: [], rxModelDataList: [], rxCpadDataList: [], stimulusPatternDataList: [] }
  }

  const { txModel = {}, termination = {}, ctleModel = {}, rxCpadModel = {}, rxModel = {} } = hspiceSignalConfig;
  const rxModelInfo = rxModel.type === SPICE ? rxModel.pairs : rxModel.pinModels,
    rxCpadModelInfo = rxCpadModel.type === SPICE ? rxCpadModel.pairs : rxCpadModel.pinModels;
  const txDataList = [], leftDataList = [], rightDataList = [], terminationDataList = [], rxCpadValueList = [], rxDataList = [];

  let controllerChannelId, deviceChannelId, findContrSignal = {}, findDeviceSignal = {};
  if (channelType === END_TO_END_CHANNEL) {
    controllerChannelId = controllerChannel.channelId;
    deviceChannelId = deviceChannel.channelId;
    const signalInfos = getContrAndDeviceSignalInfo({ signalName, signals, controllerChannelId, deviceChannelId, contrSignals, deviceSignals });
    findContrSignal = signalInfos.contrSignalInfo;
    findDeviceSignal = signalInfos.deviceSignalInfo;
  }

  for (const netName of ['nets_A', 'nets_B', 'nets_C']) {
    const contrNets = channelType === END_TO_END_CHANNEL ? findContrSignal[netName] || [] : signalInfo[netName] || [];
    const deviceNets = channelType === END_TO_END_CHANNEL ? findDeviceSignal[netName] || [] : signalInfo[netName] || [];
    // tx
    const modelValue = (txModel.pairs || []).find(item => contrNets.includes(item.net));
    if (!modelValue) {
      txDataList.push({});
    } else {
      txDataList.push({
        net: modelValue.net,
        node: modelValue.node,
        pin: modelValue.pin,
        allInfo: modelValue
      })
    }
    // termination
    const terminationValue = (termination.pinModels || []).find(item => deviceNets.includes(item.net));
    if (!terminationValue) {
      terminationDataList.push({});
    } else {
      const { value, unit } = splitValueBlankUnit(terminationValue.impedance || "50 Ohm");
      terminationDataList.push({
        pin: terminationValue.pin,
        value,
        unit
      })
    }
    // ctle
    const leftPairIndex = (ctleModel.pairs || []).findIndex(item => deviceNets.includes(item.net) && !item.originPin);
    const rightPairIndex = (ctleModel.pairs || []).findIndex(item => deviceNets.includes(item.net) && item.originPin);
    if (leftPairIndex < 0) {
      leftDataList.push({});
    } else {
      leftDataList.push({ ...ctleModel.pairs[leftPairIndex], pairIndex: leftPairIndex });
    }
    if (rightPairIndex < 0) {
      rightDataList.push({});
    } else {
      rightDataList.push({ ...ctleModel.pairs[rightPairIndex], pairIndex: rightPairIndex });
    }
    // rxCpad
    const rxCpadValue = (rxCpadModelInfo || []).find(item => deviceNets.includes(item.net)) || {};
    rxCpadValueList.push(rxCpadValue);
    // rx
    const rxValue = (rxModelInfo || []).find(item => deviceNets.includes(item.net)) || {};
    rxDataList.push(rxValue);
  }

  const signalHigh = 400 * index + (expandModelConnectVisible ? 32 : 0);
  const otherWidth = isShowStimulus ? 200 : 0;
  // termination
  const z0DataList = ["z0_A-", "z0_B-", "z0_C-"].map((keyPrefix, i) => {
    return {
      key: keyPrefix + index,
      left: 860 + otherWidth + expandModelConnectWidth,
      top: 100 * (i + 1) + signalHigh,
      pin: terminationDataList[i].pin,
      value: terminationDataList[i].value,
      unit: terminationDataList[i].unit
    }
  })
  z0DataList.push({
    key: 'Cap-' + index,
    left: 960 + otherWidth + expandModelConnectWidth,
    top: 350 + signalHigh,
    ...(splitValueBlankUnit(termination.terminationCap || "22 pF"))
  })

  // txNode
  const txNodeDataList = ["txNodeA-", "txNodeB-", "txNodeC-"].map((keyPrefix, i) => {
    return {
      key: keyPrefix + index,
      left: 110 + otherWidth,
      top: 55 + 64 * i + signalHigh + i,
      value: txDataList[i].node,
      pin: txDataList[i].pin,
      allInfo: txDataList[i].allInfo
    }
  })
  // txNet
  const txNetDataList = ["txNetA-", "txNetB-", "txNetC-"].map((keyPrefix, i) => {
    return {
      key: keyPrefix + index,
      left: 190 + otherWidth + (isShowStimulus ? 20 : 0),
      top: 40 + 64 * i + signalHigh + i,
      value: txDataList[i].net,
    }
  })

  // ctle
  const ctleDataList = ["ctleA-", "ctleB-", "ctleC-"].map((keyPrefix, i) => {
    return {
      key: keyPrefix + index,
      left: 1000 + otherWidth + expandModelConnectWidth,
      top: 10 + 98 * i + signalHigh,
      leftValue: leftDataList[i],
      rightValue: rightDataList[i]
    }
  })

  // rxModel
  const rxModelDataList = ["rxModelA-", "rxModelB-", "rxModelC-"].map((keyPrefix, i) => {
    return {
      key: keyPrefix + index,
      left: 596 + otherWidth + expandModelConnectWidth,
      top: 70 + 100 * i + signalHigh,
      modelName: rxDataList[i].modelName,
      node: rxDataList[i].node
    }
  })

  // rxCpadModel
  const rxCpadDataList = ["rxCpadA-", "rxCpadB-", "rxCpadC-"].map((keyPrefix, i) => {
    return {
      key: keyPrefix + index,
      left: 795 + otherWidth + expandModelConnectWidth,
      top: 65 + 100 * i + signalHigh,
      cpadValue: rxCpadValueList[i].cpadValue,
      node: rxCpadValueList[i].node
    }
  })

  let stimulusPatternDataList = [];
  if (isShowStimulus) {
    // stimulusPattern
    stimulusPatternDataList = ["Pattern_A", "Pattern_B", "Pattern_C"].map((keyPrefix, i) => {
      return {
        key: keyPrefix + index,
        left: 15 + otherWidth,
        top: 40 + 64 * i + signalHigh + i,
        value: keyPrefix,
      }
    })
  }


  return {
    txNodeDataList,
    txNetDataList,
    z0DataList,
    ctleDataList,
    rxModelDataList,
    rxCpadDataList,
    stimulusPatternDataList
  }
}

function getCtleNodePairList({
  pairs = [],
  signalInfo
}) {
  const leftDataList = [], rightDataList = [];
  for (const netName of ['nets_A', 'nets_B', 'nets_C']) {
    const deviceNets = signalInfo[netName] || [];
    // ctle
    const leftPairIndex = (pairs || []).findIndex(item => deviceNets.includes(item.net) && !item.originPin);
    const rightPairIndex = (pairs || []).findIndex(item => deviceNets.includes(item.net) && item.originPin);
    if (leftPairIndex < 0) {
      leftDataList.push({});
    } else {
      leftDataList.push({ ...pairs[leftPairIndex], pairIndex: leftPairIndex });
    }
    if (rightPairIndex < 0) {
      rightDataList.push({});
    } else {
      rightDataList.push({ ...pairs[rightPairIndex], pairIndex: rightPairIndex });
    }
  }
  if (pairs.length < 6) return [];
  return [
    { left: { ...leftDataList[0] }, right: { ...rightDataList[0] } },
    { left: { ...leftDataList[1] }, right: { ...rightDataList[1] } },
    { left: { ...leftDataList[2] }, right: { ...rightDataList[2] } }
  ]
}

function getSettingDataList(settingInfo = {}, eyeMaskList, userDefinedNetlistList, channelType) {
  const eyeMaskValue = settingInfo.eyeMask || {};
  const userDefinedNetlistValue = settingInfo.userDefinedNetlist || {};
  const eyeMaskNotExist = eyeMaskValue.libraryId && !eyeMaskList.find(item => item.id === eyeMaskValue.libraryId) ? true : false
  const userDefinedNetlistNotExist = userDefinedNetlistValue.libraryId && !userDefinedNetlistList.find(item => item.id === userDefinedNetlistValue.libraryId) ? true : false
  const defaultDataList = [{
    label: 'Symbol Rate',
    ...(splitValueBlankUnit(settingInfo.symbolRate)),
    type: INPUT,
    eleName: 'symbolRate',
    // suffixOptions: UNIT_OPTIONS.frequency
  }, {
    label: 'Initial Delay',
    ...(splitValueBlankUnit(settingInfo.initialDelay)),
    type: INPUTANDSELECT,
    eleName: 'initialDelay',
    suffixOptions: UNIT_OPTIONS.time
  }, {
    label: 'Time Step',
    ...(splitValueBlankUnit(settingInfo.timeStep)),
    type: INPUTANDSELECT,
    eleName: 'timeStep',
    suffixOptions: UNIT_OPTIONS.time
  }, {
    label: 'Cycles',
    value: settingInfo.cycles,
    type: INPUT,
    eleName: 'cycles',
    eleType: 'number'
  }, {
    label: 'Number of Cores',
    value: settingInfo.hspiceCores,
    type: INPUT,
    eleName: 'hspiceCores',
    eleType: 'number',
    tooltip: "Every 2 cores require one HSPICE license."
  }, {
    label: 'Eye Mask',
    value: eyeMaskNotExist ? eyeMaskValue.fileName : eyeMaskValue.libraryId || "Default",
    type: SELECT,
    eleName: 'eyeMask',
    options: eyeMaskList.map(item => ({ label: item.name, value: item.id })),
    allowClear: true,
    notExist: eyeMaskNotExist
  }, {
    label: 'Eye Plot Config',
    type: ADVANCE,
    children: [
      {
        type: "EyePlotConfig",
        label: "EyePlotConfig",
        eyePlotConfig: settingInfo.eyePlotConfig || { waveform_start: "", waveform_end: "", ui_before_trigger: "1", ui_after_trigger: "0" },
      }
    ]
  }]

  if (channelType !== END_TO_END_CHANNEL) {
    return [
      ...defaultDataList,
      {
        label: 'Netlist Advanced Option',
        type: ADVANCE,
        children: [
          {
            label: 'User Defined Netlist',
            value: userDefinedNetlistNotExist ? userDefinedNetlistValue.fileName : userDefinedNetlistValue.libraryId,
            type: SELECT,
            eleName: 'userDefinedNetlist',
            options: userDefinedNetlistList.map(item => ({ label: item.name, value: item.id })),
            allowClear: true,
            notExist: userDefinedNetlistNotExist,
            labelWidth: 320
          },
          {
            label: 'Use S-element to wrap channel touchstone',
            value: settingInfo.enableSElement,
            type: SWITCH,
            eleName: 'enableSElement',
            allowClear: true,
            tooltip: 'Enable this option will skip macro-modeling.',
            labelWidth: 320
          }
        ]
      },
      {
        label: 'Test Mode',
        value: settingInfo.testMode,
        type: SWITCH,
        eleName: 'testMode',
        tooltip: "Test mode will perform the HSPICE check Task directly and skip Modeling."
      }
    ]
  }

  return defaultDataList;
}

function getIsShow(hspiceConfig = {}) {
  const { signals = [] } = hspiceConfig;
  try {
    const isShowRx = signals[0].rxModel.type === 'None' ? false : true;
    const isShowRxCpad = signals[0].rxCpadModel.type === 'None' ? false : true;
    const isShowCtle = signals[0].ctleModel.type === 'None' ? false : true;

    const isShowStimulus = signals.some((item) => /ibis/.test(item.txModel.type))

    return { isShowRx, isShowCtle, isShowRxCpad, isShowStimulus }
  } catch (error) {
    return { isShowRx: false, isShowCtle: false, isShowRxCpad: false, isShowStimulus: false }
  }
}

function getTooltipPrefix(hspiceSignalConfig) {
  const { rxModel = {}, rxCpadModel = {} } = hspiceSignalConfig || {};
  return {
    rxPrefix: rxModel.type === SPICE ? "Node: " : "Model: ",
    rxCpadPrefix: rxCpadModel.type === SPICE ? "Node: " : "Value: "
  }
}

async function getTreeChildren(rootId) {
  const childrenRes = (await getLibraryFolderChildren(rootId)) || {};
  const childrenData = [...(childrenRes[SPICE] || []), ...(childrenRes[BUFFER_SPICE] || [])]
  // If there are no subfiles, return directly
  if (!childrenData || !childrenData.length) {
    return [];
  } else {
    const childrenTreeData = childrenData.map(item => {
      const isFolder = item.fileType.toLowerCase() === "folder"
      return {
        id: item.id,
        pId: rootId,
        title: item.name,
        value: item.id,
        selectable: !isFolder,
        isLeaf: !isFolder,
        // icon: isFolder ? "folder" : "file",
        fileInfo: item
      };
    });

    return childrenTreeData;
  }
}

async function getFileTreeData(fileTypes, files = []) {
  const fileTreeData = [];
  const pathIds = files.map(item => {
    let _pathIds = []
    if (item.path) {
      _pathIds = item.path.match(/\d+/g);
    }
    _pathIds = _pathIds.filter(item => !!item)
    return _pathIds;
  }).flat(2).filter(it => it);
  for (const type of fileTypes) {
    const fileList = libraryConstructor.getLibraryValues(type);
    for (const file of fileList) {
      const isFolder = file.fileType.toLowerCase() === "folder"
      fileTreeData.push({
        id: file.id,
        pId: type,
        title: file.name,
        value: file.id,
        selectable: !isFolder,
        isLeaf: !isFolder,
        // icon: isFolder ? "folder" : "file",
        fileInfo: { ...file, rootId: isFolder ? file.id : null }
      });
    }

    fileTreeData.push({
      id: type,
      pId: 0,
      title: type === SPICE ? "Generic Model" : "Buffer Model",
      value: type,
      selectable: false,
      isLeaf: false,
      fileInfo: {}
    });
  }


  for (const rootId of Array.from(new Set(pathIds))) {
    if (rootId) {
      try {
        const children = await getTreeChildren(rootId);
        fileTreeData.push(...children);
      } catch (error) {
        console.error(error);
      }
    }
  }

  return fileTreeData;
}

function getStimulusDataList(stimulus = {}, applyAll) {
  const labelWidth = 70;
  const tapsOptions = Array.from({ length: 7 }, (_, i) => ({ label: i + 1, value: i + 1, disabled: stimulus.length === i + 1 }));
  if (stimulus.length > 7) {
    tapsOptions.push({ label: stimulus.length, value: stimulus.length, disabled: true });
  }
  return [
    {
      label: 'Type',
      value: stimulus.type,
      type: SELECT,
      disabled: true,
      labelWidth,
      eleName: 'type',
    },
    {
      label: 'Length',
      value: stimulus.length,
      type: INPUT,
      labelWidth,
      eleName: 'length',
      eleType: 'number',
      checkError: (length) => {
        // Integers, 7 and 31
        if (!/^\d+$/.test(length)) {
          return "Length must be an integer.";
        } else if (Number(length) < 7 || Number(length) > 31) {
          return "Length must be between 7-31.";
        }
      }
    },
    {
      label: 'Taps',
      value: stimulus.taps,
      type: SELECT,
      options: tapsOptions,
      labelWidth,
      selectMode: 'multiple',
      maxTagCount: (stimulus.taps || []).length > 2 ? 1 : 2,
      maxTagPlaceholder: (omittedValues) => (
        <Tooltip
          styles={{ root: { pointerEvents: 'none' } }}
          title={omittedValues.map(({ label }) => label).join(', ')}
        >
          <span>+{(stimulus.taps || []).length - 1}</span>
        </Tooltip>
      ),
      eleName: 'taps'
    },
    {
      label: 'Seed',
      value: stimulus.seed,
      type: INPUT,
      labelWidth,
      eleName: 'seed',
      eleType: 'integer',
      checkError: (seed) => {
        // Integers, 0 to 2^length
        if (!/^\d+$/.test(seed)) {
          seed = 80;
          return "Seed must be an integer.";
        } else {
          const max = Math.pow(2, Number(stimulus.length));
          if (seed < 0) {
            seed = 80;
            return "Seed must be greater than 0.";
          } else if (Number(seed) >= max) {
            seed = 80;
            return max > 10000 ? `Seed must be less than 2 to the power of ${stimulus.length}.` : `Seed must be less than ${max}.`;
          }
        }
      }
    },
    {
      label: 'Bit length',
      value: stimulus.bitLength,
      type: INPUT,
      labelWidth,
      eleName: 'bitLength',
      eleType: 'number'
    },
    {
      label: 'Delay',
      ...(splitValueBlankUnit(stimulus.delay)),
      type: INPUTANDSELECT,
      suffixOptions: UNIT_OPTIONS.time,
      labelWidth,
      eleName: 'delay'
    },
    {
      label: "Apply setting to all signal",
      value: applyAll,
      type: CHECKBOX,
      eleName: 'applyAll',
      labelWidth: 171
    }
  ]
}

function getClickBoxPosition(canvasVisible, index, expandModelConnectWidth, expandModelConnectVisible) {
  return clickBoxPosition
    .filter((position => {
      if ((!canvasVisible.ctle && position.modalType === 'ctleModel')
        || (!canvasVisible.rxCpad && position.modalType === 'rxCpadModel')
        || (!canvasVisible.rx && position.modalType === 'rxModel')) {
        return false;
      }
      return true;
    })).map(item => {
      if (item.modalType === 'txModel' && canvasVisible.stimulus) {
        return {
          ...item,
          left: item.left + 200 + 90,
          top: item.top + 400 * index + (expandModelConnectVisible ? 32 : 0),
          className: item.className + '-exist-stimulus'
        }
      }

      return {
        ...item,
        left: item.left + (canvasVisible.stimulus ? 200 : 0) + (item.modalType === 'txModel' ? 0 : expandModelConnectWidth),
        top: item.top + 400 * index + (expandModelConnectVisible > 0 ? 32 : 0),
      }
    })
}

function splitTxModelType(type) {
  if (/ibis/.test(type)) {
    const [ibisBundle, modelType] = type.split('-');
    return { modelType, ibisBundle };
  } else {
    return { modelType: type };
  }
}

function getTxModelIBISCanvasPosition(pinNumber, signalInfo, pairs) {
  const defaultDataList = [
    { key: 'Tx-a', left: 480, top: 10 },
    { key: 'Tx-b', left: 480, top: 40 },
    { key: 'Tx-c', left: 480, top: 70 }
  ]

  let left = 45;
  const pinDataList = ['A', 'B', 'C'].map((netType, index) => {
    const nets = signalInfo[`nets_${netType}`];
    const { pin } = pairs.find(item => nets.includes(item.net)) || {};

    return Array.from({ length: pinNumber }, (_, i) => (
      {
        key: `${pin}-${netType}${i}`,
        left: left + 150 * index,
        top: 100 + 68 * i,
        suffix: `-${netType}${i}`,
        pinName: pin
      }
    ))
  }).flat();
  defaultDataList.push(...pinDataList);

  return defaultDataList;
}

function getNextNodeVisible(pairs, pairIndex, prefixNodeVisible) {
  let nodeVisible = "";
  let _nextPairIndex = -1
  if (pairs.length > 0) {
    let currentIndex = (pairIndex + 1) % pairs.length;
    while (currentIndex !== pairIndex) {
      if (!pairs[currentIndex].node && pairs[currentIndex].pin) {
        _nextPairIndex = currentIndex;
        nodeVisible = `${prefixNodeVisible}-${(pairs[_nextPairIndex] || {}).pin}`;
        break;
      }
      currentIndex = (currentIndex + 1) % pairs.length;
    }
  }
  return { _nodeVisible: nodeVisible, _nextPairIndex }
}

function getContrAndDeviceSignalInfo({
  signals,
  controllerChannelId,
  deviceChannelId,
  signalName,
  contrSignals = [],
  deviceSignals = [],
  type
}) {
  let contrSignalInfo = {}, deviceSignalInfo = {};
  if (!type || type === 'txModel') {
    const controllerSignal = getCompSignalName(signals, controllerChannelId, signalName);
    contrSignalInfo = contrSignals.find(it => it.name === controllerSignal) || {};
  }

  if (!type || type === 'ctleModel') {
    const deviceSignal = getCompSignalName(signals, deviceChannelId, signalName);
    deviceSignalInfo = deviceSignals.find(it => it.name === deviceSignal) || {};
  }

  return { contrSignalInfo, deviceSignalInfo };
}

function generateNewCompModel(comp = {}, type) {
  if (![CONNECTOR, IC, BGA, DIE].includes(comp.type)) {
    return [];
  }
  const compModels = comp.modelList || [];
  const compModelList = compModels.filter(item => item && item.type && item.type !== 'None' && item.pairs.findIndex(pair => pair.node) > -1);
  return type === 'tx' ? compModelList.reverse() : compModelList;
}

function getExpandModelConnectWidth(txModelInfoList = [], rxModelInfoList = [], resType = "object-number") {
  switch (resType) {
    case "object-number":
      return {
        txExpandConnectWidth: txModelInfoList.length * 130,
        rxExpandConnectWidth: rxModelInfoList.length * 130,
      }
    case "object-modelTypes":
      return {
        txModelTypes: txModelInfoList.map(item => ({ modelType: item.modelType })),
        rxModelTypes: rxModelInfoList.map(item => ({ modelType: item.modelType })),
      }
    case "number":
      return (txModelInfoList.length + rxModelInfoList.length) * 130 + 50;
    case "number-tx":
      return txModelInfoList.length * 130;
    default:
      return;
  }
}

function getTxAndRxModelListBySignals(components, signals, prelayoutModel, txCompInfo = {}, rxCompInfo = {}, txModelInfoList = [], rxModelInfoList = []) {
  // pck->connectorModel->modelList
  function getUsablePairs(pins, type) {
    const modelList = [];
    const modelInfoList = type === 'tx' ? txModelInfoList : rxModelInfoList;
    for (const modelInfo of modelInfoList) {
      const { pairs, modelType, files } = modelInfo;
      let fileName = "";
      const resPairs = pins.map((item = {}) => {
        const pin = pairs.find(it => it.pin === item.pin) || {};
        const pin_u = pairs.find(it => it.pin === `${item.pin}_u` || it.pin === `${item.pin}_pcb`) || {};

        const pinFileName = (files.find(it => it.libraryId === pin.libraryId) || {}).fileName;
        const pinUFileName = (files.find(it => it.libraryId === pin_u.libraryId) || {}).fileName;
        fileName = pinFileName || pinUFileName || fileName;
        return type === 'tx' ? [pin_u, pin] : [pin, pin_u];
      });

      if (resPairs.findIndex(pair => pair[0].node || pair[1].node) < 0) {
        modelList.push({ type: modelType, pairs: [] });
        continue;
      }

      modelList.push({ type: modelType, pairs: resPairs, fileName });
    }
    return modelList;
  }

  function getLayoutPinList(controllerPins, devicePins) {
    if (prelayoutModel) {
      const { pairs, files } = prelayoutModel;
      let fileName = "";
      const resPairs = controllerPins.map((contrPin = {}, index) => {
        const devicePin = devicePins[index] || {}
        const pin = pairs.find(it => it.pin === contrPin.pin && it.component === txCompInfo.name) || {};
        const pin_u = pairs.find(it => it.pin === devicePin.pin && it.component === rxCompInfo.name) || {};

        const pinFileName = (files.find(it => it.libraryId === pin.libraryId) || {}).fileName;
        const pinUFileName = (files.find(it => it.libraryId === pin_u.libraryId) || {}).fileName;
        fileName = pinFileName || pinUFileName || fileName;
        return [pin, pin_u];
      });

      if (resPairs.findIndex(pair => pair[0].node || pair[1].node) < 0) {
        return { pairs: [] };
      }
      return { pairs: resPairs, fileName }
    }

    const resPins = Array.from({ length: 3 }).map((_, i) => {
      return [controllerPins[i], devicePins[i]]
    });
    return { pairs: resPins };
  }

  const signalModelList = {};
  for (const signal of signals) {
    const { controllerPins, devicePins } = handleComponents(components, signal, txCompInfo.name, rxCompInfo.name);
    signalModelList[signal.name] = {
      tx: getUsablePairs(controllerPins, 'tx'),
      rx: getUsablePairs(devicePins, 'rx'),
      layout: getLayoutPinList(controllerPins, devicePins)
    };
  }

  return signalModelList;
}

function getTitleList({ isShowStimulus, txExpandConnectWidth, rxExpandConnectWidth, expandModelConnectVisible, designType }) {
  const defaultTitleList = [
    { type: 'tx', title: 'Tx Buffer' },
    { type: 'txComp', title: 'Tx Side', style: expandModelConnectVisible ? { width: txExpandConnectWidth + 150 } : undefined },
    { type: 'pcb-icon', title: designType === PACKAGE ? "Package" : "PCB" },
    { type: 'rxComp', title: 'Rx Side', style: expandModelConnectVisible ? { width: rxExpandConnectWidth + 150 } : undefined },
    { type: 'rx', title: 'Rx Buffer' },
    { type: 'rxCpad', title: "Rx C_Pad" },
    { type: 'termination', title: 'Termination' },
    { type: 'ctle', title: 'CTLE' }
  ]

  return isShowStimulus ? [{ type: 'stimulus', title: 'Stimulus' }, ...defaultTitleList] : defaultTitleList;
}

function getPCBBoxStyle(txExpandConnectWidth, signals, isExpand, isShowStimulus, channelType) {
  return {
    width: (isExpand ? 250 : 200),
    height: 240 + 400 * (signals.length - 1),
    left: 320 + (isExpand ? txExpandConnectWidth : 0) + (isShowStimulus ? 200 : 0),
    cursor: channelType === END_TO_END_CHANNEL ? "default" : "pointer"
  }
}

export {
  getNodeSelectOption,
  getModelNameFilterList,
  getIBISModelSelectList,
  getModelSelector,
  getSubcktByPairInfo,
  getNodeDataList,
  getCtleNodePairList,
  getSettingDataList,
  getIsShow,
  getTooltipPrefix,
  getFileTreeData,
  getStimulusDataList,
  getClickBoxPosition,
  splitTxModelType,
  getTxModelIBISCanvasPosition,
  getNextNodeVisible,
  getContrAndDeviceSignalInfo,
  getExpandModelConnectWidth,
  getTxAndRxModelListBySignals,
  generateNewCompModel,
  getTitleList,
  getPCBBoxStyle,
}